import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Minus } from 'lucide-react';
import { useFetch, fmtDateTime } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import { FeedbackSkeleton } from '../_primitives/Skeleton';
import { ErrorState } from '../_primitives/ErrorState';
import { EmptyState } from '../_primitives/EmptyState';
import type { CorrectionFeedbackRow } from '../types';

const correctionCols: Column<CorrectionFeedbackRow>[] = [
  { key: 'created_at', label: 'Datum', sortValue: (r) => r.created_at,
    render: (r) => <span className="text-ink/55 dark:text-white/55">{fmtDateTime(r.created_at)}</span> },
  { key: 'email', label: 'Gebruiker', sortValue: (r) => r.email ?? '', searchValue: (r) => r.email ?? '',
    render: (r) => <span className="text-ink/85 dark:text-white/85">{r.email ?? '—'}</span> },
  { key: 'thumbs_feedback', label: 'Feedback',
    render: (r) => {
      if (r.thumbs_feedback === 'up') return <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-semibold"><ThumbsUp size={12} /> up</span>;
      if (r.thumbs_feedback === 'down') return <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-semibold"><ThumbsDown size={12} /> down</span>;
      return <span className="inline-flex items-center gap-1 text-ink/45 dark:text-white/45 text-xs"><Minus size={12} /> —</span>;
    } },
  { key: 'mode', label: 'Mode', searchValue: (r) => r.mode ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.mode ?? '—'}</span> },
  { key: 'room_type', label: 'Ruimte', sortValue: (r) => r.room_type ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.room_type ?? '—'}</span> },
  { key: 'generation_type', label: 'Type',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.generation_type ?? '—'}</span> },
];

type SubTab = 'general' | 'correction';

export const FeedbackTab: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTab>('general');
  const general = useFetch(adminApi.feedback);
  const correction = useFetch(adminApi.correctionFeedback);

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-ink/40 rounded-xl border border-black/[0.06] dark:border-white/10 shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="flex border-b border-black/[0.06] dark:border-white/10 px-2">
          {(['general', 'correction'] as SubTab[]).map((t) => (
            <button key={t} type="button" onClick={() => setSubTab(t)}
              className={`relative px-3 py-3 text-sm font-semibold transition-colors ${subTab === t ? 'text-accent' : 'text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white'}`}
            >
              {t === 'general' ? `Algemeen (${(general.data ?? []).length})` : `Correctie-feedback (${(correction.data ?? []).length})`}
              {subTab === t && <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-accent rounded-full" />}
            </button>
          ))}
        </div>

        {subTab === 'general' && (
          <>
            {general.loading ? (
              <FeedbackSkeleton rows={5} />
            ) : general.error ? (
              <ErrorState message={general.error} onRetry={general.refresh} />
            ) : (general.data ?? []).length === 0 ? (
              <EmptyState title="Geen feedback" />
            ) : (
              <div className="divide-y divide-black/[0.05] dark:divide-white/[0.06]">
                {(general.data ?? []).map((f) => (
                  <div key={f.id} className="p-4 hover:bg-black/[0.015] dark:hover:bg-white/[0.04]">
                    <div className="flex items-center justify-between gap-4 mb-1.5">
                      <span className="text-sm font-medium text-ink dark:text-white truncate">{f.email ?? `user #${f.user_id ?? '—'}`}</span>
                      <span className="text-[11px] text-ink/45 dark:text-white/45">{fmtDateTime(f.created_at)}</span>
                    </div>
                    {f.category && <span className="inline-block text-[10px] uppercase tracking-wider text-ink/55 dark:text-white/55 bg-black/[0.04] dark:bg-white/[0.06] px-2 py-0.5 rounded-md mb-1.5">{f.category}</span>}
                    <p className="text-sm text-ink/75 dark:text-white/75 whitespace-pre-wrap leading-relaxed">{f.message}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {subTab === 'correction' && (
          <DataTable
            rows={correction.data ?? []}
            loading={correction.loading}
            error={correction.error}
            onRetry={correction.refresh}
            columns={correctionCols}
            rowKey={(r) => r.id}
            searchPlaceholder="Zoek op gebruiker, mode…"
          />
        )}
      </div>

      <Section title="Over correctie-feedback" subtitle="Ai_generation_audit met thumbs_feedback != null OR generation_type = 'correction'">
        <p className="text-sm text-ink/65 dark:text-white/65 p-5 leading-relaxed">
          Deze tab combineert algemene gebruikersfeedback (formulier-submissions) met
          AI-correctie-events. Correctie-events worden gebruikt om het preference-systeem
          te trainen — thumbs-down signalen geven hoge prioriteit voor model-tuning.
        </p>
      </Section>
    </div>
  );
};
